import { Stack } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { RHFDatePicker, RHFSelect, RHFTextField } from '@Components/hook-form';

// Styles
import Styles from './CreateRaindropContent.module.scss';
import { useGetProvidersQuery, useGetProviderConfigsLazyQuery } from '@Src/generated.graphql';
import { useEffect, useMemo } from 'react';
import { MenuItemEx } from '@Components/shared/selects/MenuItemEx';
import { useFormContext } from 'react-hook-form';
import type { CreateRaindropFormState } from '@Forms/raindrop/CreateRaindropFormContext';

const MS_IN_DAY = 1000 * 60 * 60 * 24;

const toNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getInclusiveDayDiff = (startDate: unknown, endDate: unknown): number => {
  if (!startDate || !endDate) {
    return 0;
  }

  const start = new Date(startDate as string | number | Date);
  const end = new Date(endDate as string | number | Date);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }

  const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const diff = Math.floor((endUtc - startUtc) / MS_IN_DAY);

  if (diff < 0) {
    return 0;
  }

  return diff + 1;
};

export const CreateRaindropContent = () => {
  const { setValue, getValues, watch } = useFormContext<CreateRaindropFormState>();
  const operatorId = watch('providerId');
  const freebetValue = watch('freebetValue');
  const freebetsPerRain = watch('freebetsPerRain');
  const rainsPerDay = watch('rainsPerDay');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const { data } = useGetProvidersQuery({
    onCompleted: (response) => {
      const providers = response?.providers ?? [];
      if (providers.length !== 1) {
        return;
      }

      const singleProviderId = providers[0]?.id;
      const currentOperator = getValues('providerId');

      if (singleProviderId && !currentOperator) {
        setValue('providerId', singleProviderId, { shouldValidate: true, shouldDirty: false });
      }
    },
  });

  const [getProviderConfigs, { data: providerConfigs }] = useGetProviderConfigsLazyQuery();
  const currencyOptions = useMemo(() => {
    const options = providerConfigs?.providerConfigs ?? [];
    if (!options.length) {
      return [] as string[];
    }

    const unique = new Set<string>();
    options.forEach(({ currency }) => {
      if (currency) {
        unique.add(currency);
      }
    });

    return Array.from(unique);
  }, [providerConfigs?.providerConfigs]);

  useEffect(() => {
    if (!operatorId) {
      setValue('currency', '', { shouldDirty: false, shouldValidate: false });
      return;
    }

    getProviderConfigs({ variables: { id: operatorId } });
  }, [getProviderConfigs, operatorId, setValue]);

  useEffect(() => {
    if (!operatorId) {
      return;
    }

    if (!currencyOptions.length) {
      setValue('currency', '', { shouldDirty: false, shouldValidate: false });
      return;
    }

    if (currencyOptions.length === 1) {
      const [onlyCurrency] = currencyOptions;
      if (getValues('currency') !== onlyCurrency) {
        setValue('currency', onlyCurrency, { shouldDirty: false, shouldValidate: true });
      }
    }
  }, [currencyOptions, getValues, operatorId, setValue]);

  useEffect(() => {
    const parsedFreebetValue = toNumber(freebetValue);
    const parsedFreebetsPerRain = toNumber(freebetsPerRain);
    const parsedRainsPerDay = toNumber(rainsPerDay);

    const computedDailyBudget = parsedFreebetValue * parsedFreebetsPerRain * parsedRainsPerDay || 0;

    const days = getInclusiveDayDiff(startDate, endDate);
    const computedTotalBudget = computedDailyBudget * days || 0;

    const currentDailyBudget = toNumber(getValues('dayBudget'));
    if (currentDailyBudget !== computedDailyBudget) {
      setValue('dayBudget', computedDailyBudget, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }

    const currentTotalBudget = toNumber(getValues('totalBudget'));
    if (currentTotalBudget !== computedTotalBudget) {
      setValue('totalBudget', computedTotalBudget, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [endDate, freebetValue, getValues, freebetsPerRain, rainsPerDay, setValue, startDate]);

  return (
    <Stack className={Styles.CreateRaindropContent}>
      <strong>Setup Campaign</strong>
      <Stack className={Styles.Block}>
        <RHFTextField name="name" label="Name*" className={Styles.Input} />
        <Tooltip
          title="Select the operator for which you are creating this campaign."
          placement="top"
        >
          <RHFSelect
            label="Provider"
            name="providerId"
            variant="outlined"
            className={Styles.Input}
            disabled={(data?.providers?.length ?? 0) === 1}
          >
            {data?.providers?.map((provider) => (
              <MenuItemEx key={provider.id} value={provider.id} name="Providers">
                {provider.name}
              </MenuItemEx>
            ))}
          </RHFSelect>
        </Tooltip>
        <Tooltip title="Enter the unique identifier of the casino if applicable" placement="top">
          <RHFTextField name="casinoId" label="Casino ID" className={Styles.Input} />
        </Tooltip>
      </Stack>
      <strong>Setup Raindrop</strong>
      <Stack className={Styles.Block}>
        <RHFDatePicker name="startDate" label="Start Date*" className={Styles.Input} />
        <RHFDatePicker name="endDate" label="End Date*" className={Styles.Input} />
        <Tooltip
          title="Enter the hour (0–23 UTC) when Raindrop distributions can begin each day."
          placement="top"
        >
          <RHFTextField
            name="distributionFrom"
            label="Distribution Time From*"
            className={Styles.Input}
          />
        </Tooltip>
        <Tooltip
          title="Enter the hour (0–23 UTC) when Raindrop distributions must stop each day."
          placement="top"
        >
          <RHFTextField
            name="distributionTo"
            label="Distribution Time To*"
            className={Styles.Input}
          />
        </Tooltip>
        <Tooltip
          title="Define how many freebets will be dropped in one Raindrop event."
          placement="top"
        >
          <RHFTextField
            name="freebetsPerRain"
            label="Number of Freebets Per Rain*"
            className={Styles.Input}
          />
        </Tooltip>
        <Tooltip title="Set the number of Raindrop events allowed per day." placement="top">
          <RHFTextField name="rainsPerDay" label="Rain Counts Per Day*" className={Styles.Input} />
        </Tooltip>
        <RHFTextField name="freebetValue" label="Freebet Value*" className={Styles.Input} />
        <Tooltip title="Select the campaign currency available for this operator." placement="top">
          {/* <RHFTextField name="currency" label="Currency*" className={Styles.Input} /> */}
          <RHFSelect label="Currency" name="currency" variant="outlined" className={Styles.Input}>
            {providerConfigs?.providerConfigs?.map((providerConfig) => (
              <MenuItemEx key={providerConfig.id} value={providerConfig.currency} name="Currencies">
                {providerConfig.currency}
              </MenuItemEx>
            ))}
          </RHFSelect>
        </Tooltip>
        <RHFTextField
          name="claimLimitNumber"
          label="Claim Limitation Number"
          className={Styles.Input}
        />
        <RHFSelect
          label="Claim Limitation Period"
          name="claimLimitPeriod"
          variant="outlined"
          className={Styles.Input}
        >
          <MenuItemEx value="RAIN_DURATION" name="Claim Limitation Period">
            Rain duration
          </MenuItemEx>
          <MenuItemEx value="FIVE_SECONDS" name="Claim Limitation Period">
            5s
          </MenuItemEx>
          <MenuItemEx value="TEN_SECONDS" name="Claim Limitation Period">
            10s
          </MenuItemEx>
          <MenuItemEx value="THIRTY_SECONDS" name="Claim Limitation Period">
            30s
          </MenuItemEx>
          <MenuItemEx value="ONE_MINUTE" name="Claim Limitation Period">
            1m
          </MenuItemEx>
          <MenuItemEx value="FIVE_MINUTES" name="Claim Limitation Period">
            5m
          </MenuItemEx>
        </RHFSelect>
        <Tooltip
          title="The minimum target multiplier required for the freebet to be cashed out."
          placement="top"
        >
          <RHFTextField name="minMultiplier" label="Min Multiplier*" className={Styles.Input} />
        </Tooltip>
        <Tooltip
          title="The date until which the freebets are valid, in ISO 8601 date format."
          placement="top"
        >
          <RHFDatePicker
            name="freebetValidityDate"
            label="Freebet Validity Date"
            className={Styles.Input}
          />
        </Tooltip>
        <Tooltip
          title="Define how many minutes players have to claim after the Raindrop is sent."
          placement="top"
        >
          <RHFTextField
            name="claimDeadlineMinutes"
            label="Claim Deadline (minutes)*"
            className={Styles.Input}
          />
        </Tooltip>
      </Stack>
      <strong>Campaign Budget</strong>
      <Stack className={Styles.Block}>
        <Tooltip
          title="Displays the total estimated daily spend based on rain configuration. Calculated automatically; not editable."
          placement="top"
        >
          <RHFTextField
            name="dayBudget"
            label="Daily Budget"
            className={Styles.Input}
            disabled={true}
          />
        </Tooltip>
        <Tooltip
          title="Displays the total estimated campaign spend based on daily budget and campaign length. Calculated automatically; not editable."
          placement="top"
        >
          <RHFTextField
            name="totalBudget"
            label="Total Budget"
            className={Styles.Input}
            disabled={true}
          />
        </Tooltip>
      </Stack>
      <strong>Setup Wager</strong>
      <Stack className={Styles.Block}>
        <Tooltip
          title="Set the minimum player balance needed to be eligible for claiming a drop."
          placement="top"
        >
          <RHFTextField
            name="playerMinBalance"
            label="Player Min Balance"
            className={Styles.Input}
          />
        </Tooltip>
        <Tooltip
          title="Specify the minimum amount a player must wager to qualify for claiming."
          placement="top"
        >
          <RHFTextField name="playerMinWager" label="Player Min Wager" className={Styles.Input} />
        </Tooltip>
        <Tooltip
          title="Define the time window during which the required wager must be met"
          placement="top"
        >
          <RHFTextField name="wagerPeriod" label="Wager Period" className={Styles.Input} />
        </Tooltip>
      </Stack>
    </Stack>
  );
};
